<template>
  <v-flex class="text-sm-center text-left" style="position: relative; height: 20.8px;">
    <template v-if="isTrialActive">
        <span class="hidden-xs-only">You are using trial version</span>
        <span class="hidden-sm-and-up">Trial version</span>{{trialDays}}
    </template>
    <template v-if="trialExpired">
        <span class="hidden-xs-only">Your trial period has expired.</span>
        <span class="hidden-sm-and-up">Trial Expired.</span>
    </template>
    <template v-if="subscriptionExpired">
        <span class="hidden-xs-only">Your Subscription Expired.</span>
        <span class="hidden-sm-and-up">Subscription Expired.</span>
    </template>
    <v-btn 
        class="text-capitalize ma-0" 
        small 
        color="primary"
        style="position: absolute; right: 0; top: -4px;"
        @click="subscribeModal"
    >
        <v-icon size="20" class="mr-1 hidden-sm-and-down">done</v-icon><span class="hidden-sm-and-up">Subscribe</span><span class="hidden-xs-only">Subscribe Now</span>
    </v-btn>

    <!-- Subscribe Now Dialog -->
    <v-dialog v-if="subscribeDialog" v-model="subscribeDialog" max-width="500px" persistent>
      <v-card class="pa-4">
          <v-form ref="subscribeForm" v-model="formValidation" lazy-validation>
              <v-card-title>
                  <h2 class="title">Subscribe Now</h2>
              </v-card-title>
              <div class="pa-4">
                <div class="dialog-heading mb-4 mt-2">
                  Pricing Plan Info
                </div>
                <v-flex class="xs12 px-2">
                  <v-alert dense outlined type="info" icon="info" class="mb-3">
                    <div class="body-2" style="color: rgba(0, 0, 0, 0.87)">
                      You will be charged per active staff member at the end of your billing cycle. You may always deactivate/activate staff members yourself within the staff list.
                    </div>
                  </v-alert>
                </v-flex>
                <v-flex class="xs12 px-2">
                  <v-autocomplete
                      :items="pricingPlans"
                      label="Pricing Plan*"
                      :item-text="i => `${i.product_name} - ${i.pricing_plan_name} - $${pricePerStaff(i.id).toFixed(2)}/staff/month`"
                      item-value="id"
                      v-model="subscribeData.pricing_plan_id"
                      :required="true"
                      placeholder="Please Select"
                      :error-messages="errors($v.subscribeData.pricing_plan_id)"
                      @blur="$v.subscribeData.pricing_plan_id.$touch()"
                      @change="$v.subscribeData.pricing_plan_id.$touch()"
                  >
                  </v-autocomplete>
                <div class="mt-1 text-caption">
                  <a href="https://www.sweepandgo.com/pooper-scooper-app/pricing/" target="_blank">Learn more about our pricing plans.</a>
                </div>
                </v-flex>
                <div class="dialog-heading mb-4 mt-4">
                  Credit Card Info
                </div>
                <v-flex class="xs12 px-2">
                    <card-form
                      processor="stripe"
                      @focus="resetServerValidation"
                      @resetServer="resetServerValidation"
                      @token="submitSubscriptionData($event)"
                      @state="stripe_state = $event"
                      :formBus="formBus"
                    ></card-form>
                </v-flex>
                <div class="dialog-heading mb-4 mt-2">
                  Coupon Code
                </div>
                <v-flex class="xs12 px-2">
                  <v-text-field
                      label="Coupon Code"
                      v-model="coupon_code"
                      placeholder="Please Enter"
                      hint="(Optional)"
                      persistent-hint
                      :error-messages="coupon_error_message"
                      :loading="coupon_loading"
                      :success-messages="coupon_success_message"
                  ></v-text-field>
                </v-flex>
                <estimated-charges
                  v-if="subscribeData.pricing_plan_id"
                  :pricingPlans="pricingPlans"
                  :selectedPricingPlanId="subscribeData.pricing_plan_id"
                  :coupon="this.coupon_code !== '' ? coupon : null"
                ></estimated-charges>
                <template v-if="surveyQuestions.length > 0">
                  <div class="dialog-heading mb-4 mt-6">
                    Sweep&amp;Go Survey
                  </div>
                  <v-flex 
                    v-for="(question, index) in surveyQuestions"
                    :key="index"
                    class="xs12 px-2 mb-4"
                  >
                    <div class="custom-input-lable" :class="{'primary--text': selectedQuestion === `question-${question.organization_survey_question_id}` }">{{ question.question }}</div>
                    <v-text-field
                      :ref="`question-${question.organization_survey_question_id}`"
                      :id="`question-${question.organization_survey_question_id}`"
                      placeholder="Please Enter"
                      label=""
                      :persistent-hint="question.required == 0"
                      :hint="question.required == 0 ? '(Optional)' : ''"
                      v-model="surveyAnswers[`id_${question.organization_survey_question_id}`]"
                      :error-messages="errors($v.surveyAnswers[`id_${question.organization_survey_question_id}`])"
                      @focus="selectedQuestion = `question-${question.organization_survey_question_id}`"
                      @blur="selectedQuestion = null"
                    >
                    </v-text-field>
                  </v-flex>
                </template>
                  <v-alert
                    class="pa-2 mt-4"
                    :value="
                      $v.subscribeData.$error ||
                      $v.surveyAnswers.$error ||
                      stripe_state.cardErrorMessage.length > 0 ||
                      stripe_state.nameErrorMessage.length > 0 ||
                      stripe_state.postalErrorMessage.length > 0
                    "
                    outlined
                    color="error"
                    icon="priority_high"
                    transition="scale-transition"
                  >
                    <span class="caption">Please fill out all required fields correctly</span>
                  </v-alert>
                  <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
                      <span class="caption">{{ serverValidationMessage }}</span>
                  </v-alert>
              </div>
              <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="closeSubscribe">Cancel</v-btn>
                  <v-btn color="primary" :loading="saveSubscribeLoading" :disabled="saveSubscribeLoading" @click="subscribe">Subscribe</v-btn>
              </v-card-actions>
          </v-form>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import Vue from "vue";
import CardForm from '@/components/CardForm/CardForm.vue'
import { mapGetters, mapState } from "vuex"
import {CHECK_AUTH} from "@/store/actions.type"
import { mapActions } from 'vuex'
import ApiService from "@/common/api.service"
import EventBus from '@/main'
import EstimatedCharges from '@/components/Settings/SubscriptionDetails/EstimatedCharges'
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'
import { valMessages, getValMessage } from '@/common/error-messages'
import _ from 'lodash'

let validations = {}

export default {
  name: 'SubscrubeNow',
    components: {
      'card-form': CardForm,
      EstimatedCharges
    },
  mixins: [validationMixin],
  validations() {
    validations = {
      subscribeData: {
        pricing_plan_id: { required }
      },
      surveyAnswers: {}
    }
    if(this.type === 'company') { validations.registration.company_name = { required } }
    this.surveyQuestions.forEach(i => {
      if(i.required == 1) { validations.surveyAnswers[`id_${i.organization_survey_question_id}`] = { required } }
      else { validations.surveyAnswers[`id_${i.organization_survey_question_id}`] = {} }
    })
    this.errors = getValMessage(valMessages)(validations)
    return validations
  },
  data: () => ({
    no_data: [null, '', undefined, 'null'],
    formBus: new Vue(),
    pricingPlans: null,

    coupon_code: '',
    coupon_error_message: '',
    coupon_success_message: '',
    coupon_loading: false,
    coupon: null,
    
    stripe_state: {
      cardErrorMessage: '',
      nameErrorMessage: '',
      postalErrorMessage: '',
      complete: false
    },

    surveyLoading: false,
    surveyQuestions: [],
    surveyAnswers: {},
    selectedQuestion: null,
    
    subscribeDialog: false,
    saveSubscribeLoading: false,
    subscribeData: {},
    formValidation: true,
    serverValidation: true,
    serverValidationMessage: 'Oops something went wrong...',
    unsubscribe: null
  }),
  created () {
    this.unsubscribe = this.$store.subscribeAction(action => {
      if (action.type === 'vEmit' && action.payload.name === 'subscribe') {
          this.subscribeModal()
      }
    })
  },
  mounted () {
    this.getPricingPlans()
    this.getSurvey()
  },
  beforeDestroy () {
    this.unsubscribe()
  }, 
  methods: {
    // ...mapActions(["checkAuth"]),
    ...mapActions(["vEmit"]),
    getPricingPlans() {
        ApiService.get('/organization_subscription/pricing_plans_list')
            .then(response => {
                this.pricingPlans = response.data.pricing_plans
            })
            .catch(error => {
                console.log(error)
            })
    },
    getSurvey() {
      this.surveyLoading = true
      ApiService.get('/register_organization/survey_step_questions', {step: 'subscribing'})
        .then(response => {
          let surveyQuestions = response.data.questions
          this.surveyQuestions = surveyQuestions
          this.surveyLoading = false
        })
        .catch(error => {
          console.log(error)
          this.surveyLoading = false
        })
    },
    askForStripeToken() {
        this.formBus.$emit('gaveMeToken')
    },
    subscribeModal() {
        this.subscribeData = {}
        this.surveyAnswers = {}
        this.coupon_code = ''
        this.formValidation = true
        this.resetServerValidation()
        this.$v.subscribeData.$reset()
        this.stripe_state = {
          cardErrorMessage: '',
          nameErrorMessage: '',
          postalErrorMessage: '',
          complete: false
        },
        this.subscribeDialog = true;
    },
    checkCoupon(){
        if(this.coupon_code !== ''){
            this.coupon = null
            this.coupon_error_message = ''
            this.coupon_success_message = ''
            const body = {
                coupon_id: this.coupon_code,
            }
            this.coupon_loading = true
            ApiService.put( '/organization_subscription/coupon/find', body )
                .then (response => {
                    this.coupon_loading = false
                    this.coupon = response.data.coupon
                    if(response.data.coupon == null){
                        this.coupon_error_message = 'Coupon is not valid'
                        this.coupon_success_message = ''
                    } else {
                        this.coupon_error_message = ''
                        this.coupon_success_message = 'Coupon is valid'
                    }
                }
            ).catch(error => {
                this.coupon_loading = false
                this.coupon_error_message = 'Oops something went wrong...'
                this.coupon_success_message = ''
                // this.coupon_response_val = [];
            })
        } else {
            this.coupon_error_message = ''
            this.coupon_success_message = ''
            // this.coupon_response_val = [];
        }
    },
    subscribe() {
      this.resetServerValidation()
      this.$v.subscribeData.$touch()
      this.$v.surveyAnswers.$touch()

      if(!this.$v.subscribeData.$invalid && !this.$v.surveyAnswers.$invalid){
        this.askForStripeToken()
        if(this.stripe_state.complete) {
          this.saveSubscribeLoading = true
        }
      } else {
        if(!this.stripe_state.complete) {
          this.askForStripeToken()
        }
      }
    },
    submitSubscriptionData(token) {
        let data = this.subscribeData;
        data.coupon = this.coupon_code
        if(token.error === undefined) {
            data = { ...data, ...token }
        } else {
          this.saveSubscribeLoading = false
          // this.serverValidation = false;
          // if(token.error && token.error.message) { this.serverValidationMessage = token.error.message }
          return
        }
        let answers = this.surveyQuestions.map(i => ({ 
          organization_survey_question_id: i.organization_survey_question_id,
          answer: this.surveyAnswers[`id_${i.organization_survey_question_id}`] || null
        }))
        ApiService.put('/organization_subscription/subscription', data)
            .then(resposne => {
                this.$store.dispatch(CHECK_AUTH)
                    .then(() => {
                        this.closeSubscribe();
                        this.saveSubscribeLoading = false
                        if (this.$route.name === 'subscription_details') {
                          EventBus.$emit('refresh_subscription_details')
                        }
                        this.vEmit({ 
                          name: "notification", 
                          data: {
                            status: 'success',
                            text: 'New Subscription has been successfully added.'
                          }
                        });
                    })
            })
            .catch(({ response }) => {
                this.saveSubscribeLoading = false
                this.serverValidation = false
                if (response && response.data && response.data.error) {
                  if (response.data.error.includes('_') && !response.data.error.includes(' ')) {
                    this.serverValidationMessage = this.titleCase(response.data.error);
                  } else {
                    this.serverValidationMessage = response.data.error;
                  }
                }
                if (response && response.data && response.data.message) {
                  this.serverValidationMessage = response.data.message;
                }
                if (response && response.data && response.data.errors) {
                  if(Array.isArray(response.data.errors) && response.data.errors.length > 0) {
                    this.serverValidationMessage = response.data.errors[0];
                  } else {
                    this.serverValidationMessage = response.data.errors;
                  }
                }
                if (this.$route.name === 'subscription_details') {
                  EventBus.$emit('refresh_subscription_details')
                }
            })
        ApiService.put('/register_organization/survey_step_questions', { answers, organization_id: this.user.organization_id })
          .then(response => {})
          .catch(error => {
            console.log(error)
          })
    },
    closeSubscribe() {
        this.subscribeDialog = false;
    },
    pricePerStaff(id) {
      let price = null
      if(!this.no_data.includes(id) &&
        !this.no_data.includes(this.active_users) &&
        !this.no_data.includes(this.pricingPlans) &&
        !this.pricingPlans.lenght > 0
      ) {
        let plan = this.pricingPlans.find(i => i.id === id)
        let from = 1
        plan.tiers.forEach((tier, index) => {
          if (index !== plan.tiers.length - 1) {
            if(Number(this.active_users) >= from && Number(this.active_users) <= tier.up_to ) {
              price = tier.unit_amount / 100
            }
            from = Number(tier.up_to) + 1
          } else {
            if(Number(this.active_users) >= from) {
              price = tier.unit_amount / 100
            }
          }
        })
      }
      return price
    },
    titleCase(str = "") {
      return _.startCase(str.replace(/_/g, " "));
    },
    resetServerValidation() {
      this.serverValidation = true
      this.serverValidationMessage = 'Oops something went wrong...'
    },
    errors: getValMessage(valMessages)(validations)
  },
  computed: {
      ...mapState({
        active_users: state => state.auth.user.active_users,
      }),
      ...mapGetters(["user", "isSubscribed", "isTrialActive", "endTrial", "trialExpired", "subscriptionExpired"]),
      trialDays() {
          const trialPeriodInDays = 15
          if(this.endTrial !== null) {
              return ', Day ' + (trialPeriodInDays - moment(this.endTrial).diff(moment(), 'days')) + ' of ' + trialPeriodInDays + '.'
          } else {
              return
          }
      }
  },
  watch: {
    coupon_code: _.debounce(function (val) {
      this.checkCoupon()
    }, 1000)
  }
}
</script>

<style>
  .dialog-heading {
      background-color: #efefef;
      padding: 10px;
  }
  .custom-input-lable {
    font-size: 13px;
    color: rgba(0,0,0,0.54);
    margin-bottom: -20px;
  }

  .custom-input-lable ~ div.v-input--is-focused {
    color: #00838F!important;
    caret-color: #00838F!important;
  }
</style>
